/**
 * LuxuryDefinition — Daymark Uptown
 *
 * Property-level copy of Weidner Classic’s luxury split. Left column is
 * the Daymark home mark; right is the 10px rule, eyebrow, LUXURY
 * headline, location body, and gallery CTA on a tiled dotted ground.
 */

import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useContentToken,
  useSlotGroup,
  EditableToken,
  EditableSlotGroup,
  RichTextContent,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { useScrollReveal } from '../hooks/useScrollReveal';
import daymarkIcon from '../assets/luxury-definition/daymark-icon-home.png';
import dotPattern from '../assets/luxury-definition/dot-pattern.png';

// ============================================================================
// Slot defs
// ============================================================================

const propertyGraphicDef: SlotGroupDefinition = {
  label: 'Property Graphic',
  description: 'Daymark home mark for the left column of THE DEFINITION OF LUXURY.',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue:
        'https://irp.cdn-website.com/dc71a017/dms3rep/multi/opt/daymark-icon-home+%281%29-691w.png',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'Daymark Uptown property mark',
    },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 0,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
};

const ctaDef: SlotGroupDefinition = {
  label: 'CTA',
  fields: {
    url: { label: 'Button URL', type: 'url', defaultValue: '/gallery' },
    label: { label: 'Button Label', type: 'text', defaultValue: 'View Photo Gallery →' },
    _target: { label: 'Link Target', type: 'text', defaultValue: '_self' },
  },
};

// ============================================================================
// Component
// ============================================================================

export function LuxuryDefinition() {
  const eyebrow = useContentToken('luxurydefinition.eyebrow');
  const headline = useContentToken('luxurydefinition.headline');
  const propertyGraphic = useSlotGroup('luxurydefinition.propertygraphic', propertyGraphicDef);
  const cta = useSlotGroup('luxurydefinition.cta', ctaDef);

  // The editor / generated override can still point at a gallery photo.
  // Serve the bundled Daymark mark unless a different custom URL is set.
  const slotImage = propertyGraphic.image || '';
  const src =
    !slotImage ||
    slotImage.includes('media.multihub.io') ||
    slotImage.includes('daymark-icon-home')
      ? daymarkIcon
      : slotImage;

  const sectionRef = useScrollReveal<HTMLElement>();

  return (
    <section ref={sectionRef} className="luxury-definition">
      <div
        className="luxury-definition__dots"
        style={{ backgroundImage: `url(${dotPattern})` }}
        aria-hidden="true"
      />
      <div className="luxury-definition__dots-wash" aria-hidden="true" />

      <div className="luxury-definition__inner">
        <div className="luxury-definition__graphic">
          <EditableSlotGroup
            groupPath="luxurydefinition.propertygraphic"
            definition={propertyGraphicDef}
            className="luxury-definition__graphic-frame"
          >
            <VibesitesImage
              url={src}
              alt={propertyGraphic.alt || 'Daymark Uptown property mark'}
              sizes="(min-width: 992px) 576px, 90vw"
              className="luxury-definition__graphic-img"
            />
          </EditableSlotGroup>
        </div>

        <div className="luxury-definition__copy">
          <div className="luxury-definition__heading-block">
            <EditableToken
              path="luxurydefinition.eyebrow"
              as="p"
              className="luxury-definition__eyebrow"
            >
              {eyebrow || 'THE DEFINITION OF'}
            </EditableToken>
            <EditableToken
              path="luxurydefinition.headline"
              as="h2"
              className="luxury-definition__headline"
            >
              {headline || 'LUXURY'}
            </EditableToken>
          </div>

          <RichTextContent
            path="luxurydefinition.location_body"
            label="Body"
            as="div"
            className="luxury-definition__body"
          />

          <EditableSlotGroup
            groupPath="luxurydefinition.cta"
            definition={ctaDef}
            className="luxury-definition__cta-wrap"
          >
            <a
              href={cta.url || '/gallery'}
              target={cta._target || '_self'}
              className="luxury-definition__cta"
            >
              {cta.label || 'View Photo Gallery →'}
            </a>
          </EditableSlotGroup>
        </div>
      </div>

      <style>{`
        .luxury-definition {
          position: relative;
          overflow: hidden;
          background: #fff;
          padding: 4.5rem 1.25rem 5rem;
        }
        @media (min-width: 768px) {
          .luxury-definition { padding: 5.5rem 2rem 6rem; }
        }
        @media (min-width: 1200px) {
          .luxury-definition { padding: 130px 40px; }
        }

        .luxury-definition__dots {
          position: absolute;
          inset: 0;
          background-repeat: repeat;
          background-size: auto;
          background-attachment: fixed;
          pointer-events: none;
        }
        .luxury-definition__dots-wash {
          position: absolute;
          inset: 0;
          background: #fff;
          opacity: 0.3;
          pointer-events: none;
        }

        .luxury-definition__inner {
          position: relative;
          z-index: 1;
          max-width: 1200px;
          margin: 0 auto;
          display: grid;
          gap: 2.5rem;
          align-items: center;
        }
        @media (min-width: 992px) {
          .luxury-definition__inner {
            grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
            gap: 1.5rem 2rem;
          }
        }

        .luxury-definition__graphic {
          display: flex;
          justify-content: center;
          min-width: 0;
        }
        @media (min-width: 992px) {
          .luxury-definition__graphic { padding: 50px 0; }
        }

        .luxury-definition__graphic-frame {
          display: block;
          width: 100%;
          max-width: 576px;
        }
        .luxury-definition__graphic-img {
          display: block;
          width: 100%;
          height: auto;
          aspect-ratio: 1;
          object-fit: contain;
          object-position: center;
        }

        .luxury-definition__copy {
          display: flex;
          flex-direction: column;
          justify-content: center;
          min-width: 0;
        }
        @media (min-width: 992px) {
          .luxury-definition__copy { padding-left: 30px; }
        }

        .luxury-definition__heading-block {
          border-left: 10px solid #54595f;
          padding: 0 0 0 30px;
          text-align: left;
        }

        .luxury-definition__eyebrow {
          margin: 0;
          font-family: var(--vibe-font-heading);
          font-size: 23px;
          font-weight: 400;
          letter-spacing: 0.02em;
          line-height: 1.3;
          text-transform: uppercase;
          color: var(--vibe-color-primary);
        }

        .luxury-definition__headline {
          margin: 0;
          font-family: var(--vibe-font-heading);
          font-size: 50px;
          font-weight: 700;
          letter-spacing: 0;
          line-height: 1;
          text-transform: uppercase;
          color: #b6b6b6;
        }
        @media (min-width: 768px) {
          .luxury-definition__headline { font-size: 80px; }
        }
        @media (min-width: 1200px) {
          .luxury-definition__headline { font-size: 100px; }
        }

        .luxury-definition__body {
          margin: 42px 0 8px;
          font-family: var(--vibe-font-body);
          font-size: 15px;
          font-weight: 400;
          line-height: 2;
          color: #000;
          max-width: 36rem;
          text-align: left;
        }
        .luxury-definition__body p { margin: 0; }

        .luxury-definition__cta-wrap {
          display: block;
          width: fit-content;
          margin: 30px 0 10px;
          transform-origin: center center;
        }

        .luxury-definition__cta {
          display: flex;
          align-items: center;
          justify-content: center;
          box-sizing: border-box;
          min-width: 16.5rem;
          height: 60px;
          padding: 0 1.5rem;
          background: var(--vibe-color-secondary);
          color: var(--vibe-color-text-on-secondary);
          font-family: var(--vibe-font-body);
          font-size: 16px;
          font-weight: 700;
          letter-spacing: 0.04em;
          text-transform: uppercase;
          text-decoration: none;
          border: none;
          border-radius: 0;
          transition: background 0.25s ease, color 0.25s ease;
        }
        .luxury-definition__cta:hover {
          background: #b6b6b6;
          color: #000;
        }

        /* Copy-column reveals. useScrollReveal toggles reveal--hidden /
           reveal--visible on the section; children start at rest so
           SSG/SSR never flash off-screen. Heading slides in from the
           right, body slides up, CTA scales from back — same recipe
           as TabbedCallouts. */
        .luxury-definition__heading-block,
        .luxury-definition__body,
        .luxury-definition__cta-wrap {
          transition: opacity 1.2s cubic-bezier(0.22, 1, 0.36, 1),
                      transform 1.2s cubic-bezier(0.22, 1, 0.36, 1);
        }
        .luxury-definition.reveal--visible .luxury-definition__body {
          transition-delay: 0.2s;
        }
        .luxury-definition.reveal--visible .luxury-definition__cta-wrap {
          transition-delay: 0.45s;
        }
        .luxury-definition.reveal--hidden .luxury-definition__heading-block {
          opacity: 0;
          transform: translateX(36px);
        }
        .luxury-definition.reveal--hidden .luxury-definition__body {
          opacity: 0;
          transform: translateY(36px);
        }
        .luxury-definition.reveal--hidden .luxury-definition__cta-wrap {
          opacity: 0;
          transform: scale(0.6);
        }
        .luxury-definition.reveal--visible .luxury-definition__heading-block,
        .luxury-definition.reveal--visible .luxury-definition__body,
        .luxury-definition.reveal--visible .luxury-definition__cta-wrap {
          opacity: 1;
          transform: none;
        }

        @media (prefers-reduced-motion: reduce) {
          .luxury-definition__dots { background-attachment: scroll; }
          .luxury-definition__heading-block,
          .luxury-definition__body,
          .luxury-definition__cta-wrap,
          .luxury-definition.reveal--hidden .luxury-definition__heading-block,
          .luxury-definition.reveal--hidden .luxury-definition__body,
          .luxury-definition.reveal--hidden .luxury-definition__cta-wrap {
            opacity: 1;
            transform: none;
            transition: none;
          }
        }
      `}</style>
    </section>
  );
}

// ============================================================================
// Content Schema
// ============================================================================

export const contentSchema: ComponentContentSchema = {
  slot: 'LuxuryDefinition',
  displayName: 'Luxury Definition',
  tokens: {
    'luxurydefinition.eyebrow': {
      label: 'Eyebrow',
      type: 'text',
      defaultValue: 'THE DEFINITION OF',
    },
    'luxurydefinition.headline': {
      label: 'Headline',
      type: 'text',
      defaultValue: 'LUXURY',
    },
    'luxurydefinition.location_body': {
      label: 'Body',
      type: 'richtext',
      defaultValue:
        '<p>Daymark is ideally located in the heart of Uptown in one of the most walkable communities in Minneapolis. In fact, you’ll live within walking distance of your favorite parks, restaurants, grocery stores, the Chain of Lakes, and we have dedicated retail space on the first floor of our building. It’s an excellent choice for busy professionals who commute to Downtown. In addition, you can quickly jump on nearby I-35, I-94, and Hwy 100 to get to work or school, including The University of Minnesota just 15 minutes away. This community is perfect for foodies who want to be close to a variety of different restaurants such as Amazing Thai and Agra Culture Kitchen. Right across the lake is a popular dog park, and you’ll live just a short drive from Target, The Galleria, and The Mall of America for your shopping needs. You’ll also be surrounded by many local parks such as Lake of the Isles Park and Kenwood Park, both popular options for water sports and outdoor recreational activities.</p>',
    },
  },
  slotGroups: {
    'luxurydefinition.propertygraphic': propertyGraphicDef,
    'luxurydefinition.cta': ctaDef,
  },
};

(LuxuryDefinition as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default LuxuryDefinition;
